import React from 'react';
import { Alert, AlertTitle, Box, Typography, type AlertColor } from '@mui/material';
import {
  HST_PROVISIONING_STATUS_LABELS,
  hstProvisioningDisplayStatus,
  type HstAccessRequestView,
  type HstProvisioningDisplayStatus,
  type HstProvisioningResultView,
} from '@trackly/shared';
import { useFeatureFlag } from '../../../../../utils/featureFlags';
import { useGetHstCentersQuery } from '../../../../../services/DimensionService/dimensionApi';

interface HstProvisioningStatusProps {
  request?: HstAccessRequestView;
  ticketStatus?: string;
}

const STATUS_SEVERITY: Record<HstProvisioningDisplayStatus, AlertColor> = {
  Pending: 'info',
  Provisioned: 'success',
  Failed: 'error',
  ManualProvisioningNeeded: 'warning',
};

const PENDING_MESSAGE = 'Waiting for the HST provisioning step to run.';

interface DetailRow {
  label: string;
  value: string;
}

const detailRows = (result: HstProvisioningResultView, centerNames: string[]): DetailRow[] => [
  { label: 'HST user ID', value: result.hstUserId },
  { label: 'Centers', value: centerNames.join(', ') },
  { label: 'Reason', value: result.reason ?? '' },
  { label: 'Time', value: result.attemptedAt ? new Date(result.attemptedAt).toLocaleString() : '' },
];

/**
 * HstProvisioningStatus - Read-only provisioning result for an HST access request (spec 012, FR-016).
 * Renders nothing when the HstProvisioning flag is off, when the ticket is not an HST request, or
 * when a finished ticket has no result (FR-017).
 */
export const HstProvisioningStatus: React.FC<HstProvisioningStatusProps> = ({ request, ticketStatus }) => {
  const enabled = useFeatureFlag('HstProvisioning');
  const { data: centers } = useGetHstCentersQuery(undefined, { skip: !enabled });
  const status = enabled ? hstProvisioningDisplayStatus(request, ticketStatus) : null;

  if (!status) return null;

  const result = request?.provisioning;
  const centerNames = (result?.centers ?? []).map((code) => centers?.find((center) => center.value === code)?.name ?? code);
  const rows = result ? detailRows(result, centerNames).filter((row) => row.value) : [];

  return (
    <Alert severity={STATUS_SEVERITY[status]} role="status">
      <AlertTitle>{HST_PROVISIONING_STATUS_LABELS[status]}</AlertTitle>
      {rows.length === 0 && <Typography variant="body2">{PENDING_MESSAGE}</Typography>}
      {rows.map((row) => (
        <Box key={row.label} sx={{ display: 'flex', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.label}:
          </Typography>
          <Typography variant="body2">{row.value}</Typography>
        </Box>
      ))}
    </Alert>
  );
};

export default HstProvisioningStatus;
